import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Users, Shield, Heart, Clock, ChevronRight, 
  User, Home, BookOpen, ArrowRight,
  HelpCircle, Eye
} from 'lucide-react';

/**
 * LIKEKIND HOME PAGE (PART A — FINAL LOCKED DIRECTIVE)
 * 
 * Required:
 * - What LikeKind is (1–2 short paragraphs)
 * - Who it's for (Adults, Families, Groups, Schools)
 * - Clear "Enter" paths:
 *   - Join as Individual
 *   - Join as Family
 *   - Join as School / Program
 *   - Explore SCUTA (read-only)
 */

export default function LandingPageNew() {
  const navigate = useNavigate();

  const entryPaths = [
    {
      id: 'individual',
      icon: User,
      title: 'Join as Individual',
      description: 'Adults (18+) who want to connect locally',
      color: 'var(--lk-primary-green)',
      path: '/for/individuals',
      cta: 'Get Started'
    },
    {
      id: 'family',
      icon: Home,
      title: 'Join as Family',
      description: 'Parents with children or teens',
      color: 'var(--lk-secondary-orange)',
      path: '/for/families',
      cta: 'Start as Parent'
    },
    {
      id: 'school',
      icon: BookOpen,
      title: 'Join as School / Institution',
      description: 'Schools, programs, nonprofits, organizations',
      color: 'var(--lk-earth-brown)',
      path: '/membership',
      cta: 'Get Started'
    },
    {
      id: 'scuta',
      icon: Clock,
      title: 'Explore SCUTA',
      description: 'View historical timelines (no login required)',
      color: '#C2A45E',
      path: '/scuta',
      cta: 'Explore Timelines',
      secondary: true
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-sm" style={{ backgroundColor: 'rgba(244, 239, 230, 0.95)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_kindplatform/artifacts/my73uiau_file_00000000f09c720c9463ab506718d330.png" 
                alt="LikeKind" 
                className="w-10 h-10 rounded-xl"
              />
              <span className="text-xl font-black" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>LikeKind</span>
            </div>
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/faq/individuals')}
                className="text-sm font-medium hidden sm:block"
                style={{ color: 'var(--fy-text-secondary)' }}
              >
                FAQ
              </button>
              <Button 
                onClick={() => navigate('/auth')}
                className="rounded-full"
                style={{ backgroundColor: 'var(--lk-primary-green)', color: 'var(--lk-warm-sand)' }}
                data-testid="home-signin-btn"
              >
                Sign In
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="max-w-5xl mx-auto px-4 pt-12 pb-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>
            A place for people who want to be present.
          </h1>
          <p className="text-lg leading-relaxed mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
            LikeKind is for people who want to connect with others — through groups, activities, 
            and shared interests — without algorithms, engagement tricks, or data harvesting.
          </p>
          <p className="text-base" style={{ color: 'var(--fy-text-secondary)' }}>
            Everything here is opt-in. Nothing is ranked or manipulated. You decide what to share 
            and with whom.
          </p>
        </div>

        {/* Who It's For */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <span className="px-4 py-2 rounded-full text-sm font-medium" style={{ backgroundColor: 'var(--lk-sage-muted)', color: 'var(--lk-primary-green)' }}>
            <User className="w-4 h-4 inline mr-1" /> Adults
          </span>
          <span className="px-4 py-2 rounded-full text-sm font-medium" style={{ backgroundColor: 'var(--lk-terra-muted)', color: 'var(--lk-secondary-orange)' }}>
            <Home className="w-4 h-4 inline mr-1" /> Families
          </span>
          <span className="px-4 py-2 rounded-full text-sm font-medium" style={{ backgroundColor: 'rgba(139, 106, 79, 0.15)', color: 'var(--lk-earth-brown)' }}>
            <Users className="w-4 h-4 inline mr-1" /> Groups
          </span>
          <span className="px-4 py-2 rounded-full text-sm font-medium" style={{ backgroundColor: 'rgba(139, 106, 79, 0.15)', color: 'var(--lk-earth-brown)' }}>
            <BookOpen className="w-4 h-4 inline mr-1" /> Schools
          </span>
        </div>
      </section>

      {/* Entry Paths */}
      <section className="max-w-5xl mx-auto px-4 pb-12">
        <h2 className="text-2xl font-bold text-center mb-8" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>
          Choose how to enter
        </h2>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {entryPaths.filter(p => !p.secondary).map((path) => {
            const Icon = path.icon;
            return (
              <Card 
                key={path.id}
                className="p-6 cursor-pointer transition-all hover:shadow-lg group"
                style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
                onClick={() => navigate(path.path)}
                data-testid={`entry-${path.id}`}
              >
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `${path.color}20` }}
                >
                  <Icon className="w-6 h-6" style={{ color: path.color }} />
                </div>
                <h3 className="font-semibold text-lg mb-2" style={{ color: 'var(--fy-text-primary)' }}>
                  {path.title}
                </h3>
                <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
                  {path.description}
                </p>
                <Button 
                  className="w-full rounded-lg font-medium group-hover:opacity-90"
                  style={{ backgroundColor: path.color, color: 'white' }}
                >
                  {path.cta}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Card>
            );
          })}
        </div>

        {/* SCUTA Entry (Secondary) */}
        <Card 
          className="mt-6 p-6 cursor-pointer transition-all hover:shadow-lg"
          style={{ backgroundColor: '#0A0A0A', border: '1px solid #1A1A1A' }}
          onClick={() => navigate('/scuta')}
          data-testid="entry-scuta"
        >
          <div className="flex items-center gap-4">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'rgba(194, 164, 94, 0.15)', border: '1px solid #C2A45E' }}
            >
              <Clock className="w-6 h-6" style={{ color: '#C2A45E' }} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg" style={{ color: '#E6E9EE' }}>
                Explore SCUTA
              </h3>
              <p className="text-sm" style={{ color: '#B0B0B0' }}>
                View historical timelines. No login required.
              </p>
            </div>
            <Button 
              variant="outline"
              className="rounded-lg font-medium hidden sm:flex"
              style={{ borderColor: '#C2A45E', color: '#C2A45E' }}
            >
              Explore Timelines
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
            <ChevronRight className="w-6 h-6 sm:hidden" style={{ color: '#C2A45E' }} />
          </div>
        </Card>
      </section>

      {/* Safety Promise */}
      <section className="py-12" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'var(--lk-sage-muted)' }}>
              <Shield className="w-7 h-7" style={{ color: 'var(--lk-primary-green)' }} />
            </div>
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>
              Built on safety, not engagement
            </h2>
            <div className="grid sm:grid-cols-3 gap-6 mt-8 text-left">
              <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)' }}>
                <Shield className="w-5 h-5 mb-2" style={{ color: 'var(--lk-primary-green)' }} />
                <h3 className="font-semibold mb-1" style={{ color: 'var(--fy-text-primary)' }}>No algorithms</h3>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>Content is not ranked or manipulated</p>
              </div>
              <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)' }}>
                <Eye className="w-5 h-5 mb-2" style={{ color: 'var(--lk-primary-green)' }} />
                <h3 className="font-semibold mb-1" style={{ color: 'var(--fy-text-primary)' }}>Privacy first</h3>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>You control what you share and with whom</p>
              </div>
              <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)' }}>
                <Heart className="w-5 h-5 mb-2" style={{ color: 'var(--lk-primary-green)' }} />
                <h3 className="font-semibold mb-1" style={{ color: 'var(--fy-text-primary)' }}>Child protection</h3>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>Safety features are always free</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap justify-center gap-6 mb-6">
            <button onClick={() => navigate('/for/individuals')} className="text-sm hover:underline" style={{ color: 'var(--fy-text-secondary)' }}>
              For Individuals
            </button>
            <button onClick={() => navigate('/for/families')} className="text-sm hover:underline" style={{ color: 'var(--fy-text-secondary)' }}>
              For Families
            </button>
            <button onClick={() => navigate('/for/schools')} className="text-sm hover:underline" style={{ color: 'var(--fy-text-secondary)' }}>
              For Schools
            </button>
            <button onClick={() => navigate('/for/commons')} className="text-sm hover:underline" style={{ color: 'var(--fy-text-secondary)' }}>
              For Commons
            </button>
            <button onClick={() => navigate('/scuta')} className="text-sm hover:underline" style={{ color: 'var(--fy-text-secondary)' }}>
              SCUTA
            </button>
          </div>
          <div className="flex flex-wrap justify-center gap-6 mb-6">
            <button onClick={() => navigate('/faq/individuals')} className="text-sm hover:underline" style={{ color: 'var(--fy-text-disabled)' }}>
              FAQ
            </button>
            <button onClick={() => navigate('/privacy')} className="text-sm hover:underline" style={{ color: 'var(--fy-text-disabled)' }}>
              Privacy
            </button>
            <button onClick={() => navigate('/terms')} className="text-sm hover:underline" style={{ color: 'var(--fy-text-disabled)' }}>
              Terms
            </button>
            <button onClick={() => navigate('/support')} className="text-sm hover:underline" style={{ color: 'var(--fy-text-disabled)' }}>
              Contact
            </button>
          </div>
          <p className="text-center text-xs" style={{ color: 'var(--fy-text-disabled)' }}>
            &copy; {new Date().getFullYear()} LikeKind. Built for real people and real connections — not algorithms.
          </p>
        </div>
      </footer>
    </div>
  );
}
